import { z } from "zod";
import {
  buildAreaData,
  toNumber,
} from "@/components/ui/area-chart/ChatAreaChartSpec";
import BarChartView from "@/components/ui/bar-chart/BarChartView";
import type { ChartConfig } from "@/components/ui/ChartTooltip";
import { chartHexColorSchema } from "@/lib/utils/chartUtils";

type TAggregateMode = "sum" | "count" | "avg";

const yKeyLabel = (yKey: string): string =>
  yKey.length > 0 ? `${yKey.charAt(0).toUpperCase()}${yKey.slice(1)}` : yKey;

/**
 * Multi-series rows by `xKey` (StackedBarChart / ComposedChart). For `aggregate` `"count"`,
 * each segment counts rows in the bucket with a numeric value for that key.
 */
export const buildStackedBarData = (input: {
  data: Array<Record<string, unknown>>;
  xKey: string;
  seriesKeys: string[];
  aggregate: TAggregateMode | null;
}): Array<Record<string, string | number>> => {
  const { data, xKey, seriesKeys, aggregate } = input;

  if (aggregate == null) {
    return data
      .map((row) => {
        const x = row[xKey];
        if (x == null) return null;
        const out: Record<string, string | number> = { [xKey]: String(x) };
        for (const k of seriesKeys) {
          const y = toNumber(row[k]);
          if (y == null) continue;
          out[k] = y;
        }
        if (Object.keys(out).length <= 1) return null;
        return out;
      })
      .filter((row): row is Record<string, string | number> => row != null);
  }

  type Accum = {
    sums: Record<string, number>;
    counts: Record<string, number>;
  };

  const groups = new Map<string, Accum>();

  for (const row of data) {
    const xRaw = row[xKey];
    if (xRaw == null) continue;
    const x = String(xRaw);
    const acc =
      groups.get(x) ??
      ({
        sums: Object.fromEntries(seriesKeys.map((k) => [k, 0])) as Record<
          string,
          number
        >,
        counts: Object.fromEntries(seriesKeys.map((k) => [k, 0])) as Record<
          string,
          number
        >,
      } satisfies Accum);

    if (aggregate === "count") {
      for (const k of seriesKeys) {
        const y = toNumber(row[k]);
        if (y != null) {
          acc.counts[k] = (acc.counts[k] ?? 0) + 1;
        }
      }
    } else {
      for (const k of seriesKeys) {
        const y = toNumber(row[k]);
        if (y == null) continue;
        acc.sums[k] = (acc.sums[k] ?? 0) + y;
        acc.counts[k] = (acc.counts[k] ?? 0) + 1;
      }
    }
    groups.set(x, acc);
  }

  const result: Array<Record<string, string | number>> = [];
  for (const [x, acc] of groups.entries()) {
    const row: Record<string, string | number> = { [xKey]: x };
    for (const k of seriesKeys) {
      const sumVal = acc.sums[k] ?? 0;
      const countVal = acc.counts[k] ?? 0;
      if (aggregate === "count") {
        row[k] = countVal;
      } else if (aggregate === "avg") {
        row[k] = countVal === 0 ? 0 : Number((sumVal / countVal).toFixed(2));
      } else {
        row[k] = Number(sumVal.toFixed(2));
      }
    }
    result.push(row);
  }
  return result;
};

const PALETTE = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
] as const;

const colorForSeriesKey = (key: string, paletteIndex: number): string => {
  const l = key.toLowerCase();
  if (l.includes("positive")) return "var(--sentiment-positive)";
  if (l.includes("negative")) return "var(--sentiment-negative)";
  if (l.includes("neutral")) return "var(--sentiment-neutral)";
  const idx = paletteIndex % PALETTE.length;
  return PALETTE[idx] ?? "var(--chart-1)";
};

/** Sentiment order for stacked/composed series keys. */
export const sortSeriesBySentimentOrder = <
  T extends { key: string; label?: string | null | undefined },
>(
  series: T[],
): T[] => {
  const rank = (key: string): number => {
    const k = key.toLowerCase();
    if (k.includes("positive")) return 0;
    if (k.includes("neutral")) return 1;
    if (k.includes("negative")) return 2;
    return 3;
  };
  return [...series]
    .map((s, i) => ({ s, i }))
    .sort((a, b) => {
      const ra = rank(a.s.key);
      const rb = rank(b.s.key);
      if (ra !== rb) return ra - rb;
      return a.i - b.i;
    })
    .map(({ s }) => s);
};

export const buildStackedChartConfig = (
  series: {
    key: string;
    label?: string | null | undefined;
    color?: string | null | undefined;
  }[],
): ChartConfig => {
  const config: ChartConfig = {};
  for (const [i, s] of series.entries()) {
    const lbl = s.label ?? s.key;
    config[s.key] = {
      label: typeof lbl === "string" ? lbl : s.key,
      color: s.color ?? colorForSeriesKey(s.key, i),
    };
  }
  return config;
};

export const ChatBarChartSpec = {
  props: z.object({
    title: z.string().nullable().optional(),
    data: z.array(z.record(z.string(), z.unknown())),
    xKey: z.string(),
    yKey: z.string(),
    aggregate: z.enum(["sum", "count", "avg"]).nullable().optional(),
    height: z.number().nullable().optional(),
    colors: z.record(z.string(), chartHexColorSchema).nullable().optional(),
  }),
  description:
    'Bar chart for a single data series. Props: title (string or null), data (array of records), xKey, yKey, aggregate ("sum" | "count" | "avg" or null), height (number or null), colors (optional map of category xKey value → #RGB or #RRGGBB hex for per-bar fills). Use { "$state": "/path" } to bind read-only data. For multiple series stacked, use StackedBarChart instead.',
  example: {
    title: "Articles per publisher",
    data: [
      { publisher: "Reuters", count: 42 },
      { publisher: "AP", count: 28 },
    ],
    xKey: "publisher",
    yKey: "count",
    aggregate: null,
    height: 240,
  },
} as const;

export const ChatStackedBarChartSpec = {
  props: z.object({
    title: z.string().nullable().optional(),
    data: z.array(z.record(z.string(), z.unknown())),
    xKey: z.string(),
    series: z.array(
      z.object({
        key: z.string(),
        label: z.string().nullable().optional(),
        color: chartHexColorSchema.nullable().optional(),
      }),
    ),
    aggregate: z.enum(["sum", "count", "avg"]).nullable().optional(),
    height: z.number().nullable().optional(),
  }),
  description:
    'Stacked bar for part-to-whole across categories or time. Props: title (string or null), data (array of records), xKey, series (array of { key, label, color? } where color is optional #RGB or #RRGGBB hex), aggregate ("sum" | "count" | "avg" or null), height (number or null). Each series key is a stacked segment. When color is omitted, keys positive / negative / neutral (case-insensitive) get semantic sentiment colors; other keys use var(--chart-1)–var(--chart-5) by series index. Use { "$state": "/path" } to bind read-only data.',
  example: {
    title: "Sentiment by week",
    data: [
      { week: "2025-01-06", Negative: 12, Neutral: 28, Positive: 8 },
      { week: "2025-01-13", Negative: 10, Neutral: 32, Positive: 6 },
    ],
    xKey: "week",
    series: [
      { key: "Positive", label: "Positive" },
      { key: "Neutral", label: "Neutral" },
      { key: "Negative", label: "Negative" },
    ],
    aggregate: null,
    height: 280,
  },
} as const;

/** Parses props and renders {@link BarChartView} (single series). */
export const ChatBarChartFromProps = ({
  props,
}: {
  props: Record<string, unknown>;
}) => {
  const parsed = ChatBarChartSpec.props.safeParse(props);
  if (!parsed.success) return null;

  const { title, data, xKey, yKey, aggregate, height, colors } = parsed.data;
  const chartData = buildAreaData({
    data,
    xKey,
    yKey,
    aggregate: aggregate ?? null,
  });
  const label = yKeyLabel(yKey);
  const chartConfig = {
    [yKey]: { label, color: "var(--chart-1)" },
  } satisfies ChartConfig;

  return (
    <BarChartView
      title={title ?? null}
      data={chartData}
      chartConfig={chartConfig}
      xAxisKey={xKey}
      series={[{ key: yKey, label }]}
      layout="horizontal"
      stacked={false}
      height={height ?? null}
      categoryColors={colors ?? undefined}
    />
  );
};

/** Parses props and renders {@link BarChartView} (stacked). */
export const ChatStackedBarChartFromProps = ({
  props,
}: {
  props: Record<string, unknown>;
}) => {
  const parsed = ChatStackedBarChartSpec.props.safeParse(props);
  if (!parsed.success) return null;

  const {
    title,
    data,
    xKey,
    series: seriesSpec,
    aggregate,
    height,
  } = parsed.data;
  const sortedSeries = sortSeriesBySentimentOrder(seriesSpec);
  const seriesKeys = sortedSeries.map((s) => s.key);
  const chartData = buildStackedBarData({
    data,
    xKey,
    seriesKeys,
    aggregate: aggregate ?? null,
  });
  const chartConfig = buildStackedChartConfig(sortedSeries);

  return (
    <BarChartView
      title={title ?? null}
      data={chartData}
      chartConfig={chartConfig}
      xAxisKey={xKey}
      series={sortedSeries.map((s) => ({
        key: s.key,
        label: s.label ?? undefined,
      }))}
      layout="horizontal"
      stacked
      height={height ?? null}
    />
  );
};
